import React, {
    Fragment,
    useMemo,
    memo,
    useEffect,
    useLayoutEffect,
    useReducer,
    useCallback,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import { createUseStyles, Icon, Button } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';
import { LANGUAGES } from 'lib/helpers';
import { getExtraField, createExtraField, updateExtraField, getTabs } from 'lib/services/fields';
import {
    formatDynamicFields,
    formatDependenceFields,
    normalizeFieldName,
    getTabsOptions,
    isEntityList,
    DUPLICATES_SECTION_SCHEMA,
    DUPLICATES_SECTION_SCHEMA_DISABLED,
    ENABLED_DUPLICATE_ENTITIES,
} from '../lib';
import { getFieldTypesList } from 'lib/literals';
import { getFieldsValidations, getFieldValidation } from 'lib/services/fieldsValidations';
import { getParameter } from 'lib/services/parameters';

import { showErrorToast } from 'lib/errors';

import { CRUD_TYPE_ADD, CRUD_TYPE_EDIT, CRUD_TYPE_DUPLICATE } from 'lib/constants';

import ForceForm, { validate, flatList } from 'components/ForceForm';
import HyperDrawer from 'components/HyperDrawer';
import Tour from 'components/Tour';
import FormValueList from 'shards/ValueLists/FormValueList';
import FormFieldsValidations from 'shards/FieldsValidations/FormFieldsValidations';
import FormTab from '../FormTab';
import FormSmartFieldConfig from '../FormSmartFieldConfig';
import { SmartFieldsConfirmModal, SmartFieldsValuesSection } from '../components';
import { getDescription } from 'utils/custom';
import { executeSandbox } from 'utils/sandbox';
import { searchValues, getList } from 'lib/services/valueLists';
import { successToast } from 'utils/toast';
import { useSmartFieldsValues, useSmartFieldsToggle } from '../hooks';

import styles from './styles';

const useStyles = createUseStyles(styles, 'FormTab');

const FIELD_TYPE = {
    multivalue: 'checkboxGroup',
    list: 'valueList',
    text: 'text',
    decimal: 'decimals',
    int: 'integer',
    currency: 'currency',
    datetime: 'dateTime',
    bit: 'switch',
    percentage: 'percentage',
};

const FORM_SCHEMA = {
    context: {
        fields: {
            branches: {
                table: 'tblSucursales',
            },
            dynamicFieldValues: {
                table: null,
            },
            relevantFields: {
                table: 'tblTiposUsuario',
            },
            integrationField: {
                table: 'tblIntegrationValues',
            },
        },
    },
    required: ['fieldName', 'entity', 'type', 'description', 'valueListRelated'],
    properties: {
        fieldName: {
            maxLength: 200,
            type: ['string'],
        },
        entity: {
            maxLength: 200,
            type: ['string'],
        },
        type: {
            maxLength: 200,
            type: ['string'],
        },
        description: {
            maxLength: 200,
            type: ['string'],
        },
        branches: {
            type: ['array'],
            items: { type: 'integer' },
        },
        dynamicFieldValues: {
            type: ['array'],
            items: { type: 'integer' },
        },
        customValidation: {
            type: ['integer'],
        },
        defaultValue: {
            maxLength: 200,
            type: ['string'],
        },
        length: {
            maxLength: 200,
            type: ['integer'],
        },
        valueListRelated: {
            type: ['string'],
        },
        tabId: {
            type: ['integer'],
        },
        dynamicField: {
            type: ['string'],
        },
        dependenceField: {
            type: ['string'],
        },
        visible: {
            type: ['boolean'],
        },
        mobileVisibility: {
            type: ['boolean'],
        },
        visibleInList: {
            type: ['boolean'],
        },
        searchable: {
            type: ['boolean'],
        },
        mandatory: {
            type: ['boolean'],
        },
        readOnly: {
            type: ['boolean'],
        },
        fuzzySearch: {
            type: ['boolean'],
        },
        visibleInPhoneCallForm: {
            type: ['boolean'],
        },
        restApi: {
            type: ['boolean'],
        },
        mandatoryRestApi: {
            type: ['boolean'],
        },
        integrationField: {
            type: ['integer'],
        },
        integrationFieldName: {
            type: ['string'],
        },
    },
    layout: {
        schemaId: 'createExtraField',
        uiGrid: 'linearVertical',
        composition: [
            {
                schemaId: 'entity',
                label: 'cfm_label_entity',
                uiType: 'list',
                placeholder: 'label_selectone',
            },
            {
                schemaId: 'tabId',
                label: 'cfm_label_tab',
                uiType: 'list',
                placeholder: 'label_selectone',
            },
            {
                schemaId: 'description',
                label: 'cfm_label_field_name',
                uiType: 'expandableComponent',
                labelForExpand: ['cfm_label_view_more_lang', 'cfm_label_view_less_lang'],
                composition: [
                    {
                        schemaId: 'description',
                        uiType: 'text',
                        label: 'cfm_label_field_name',
                        placeholder: 'cfm_label_spanish',
                    },
                    ...LANGUAGES.map((language) => ({
                        label: `cfm_label_${language.label}`,
                        schemaId: `description${language.key}`,
                        uiType: 'text',
                        placeholder: 'cfm_placeholder_type_value',
                    })),
                ],
            },
            {
                schemaId: 'fieldName',
                label: 'cfm_label_fieldname_api_name',
                uiType: 'text',
                placeholder: 'cfm_placeholder_type_value',
            },
            {
                schemaId: 'type',
                label: 'cfm_label_data_type',
                uiType: 'list',
                placeholder: 'label_selectone',
            },
            {
                schemaId: 'isEditable',
                label: 'cfm_label_editable_by_user',
                uiType: 'checkbox',
            },
            {
                schemaId: 'includeTime',
                label: 'cfm_label_includes_time',
                uiType: 'checkbox',
                isHidden: true,
            },
            {
                schemaId: 'valueListRelated',
                label: 'cfm_label_related_table',
                uiType: 'list',
                placeholder: 'label_selectone',
                composition: [
                    {
                        schemaId: 'buttonvalue',
                        uiType: 'button',
                        label: 'cfm_label_description',
                    },
                ],
                isHidden: true,
            },
            {
                schemaId: 'customValidation',
                label: 'cfm_label_validation',
                uiType: 'list',
                placeholder: 'label_selectone',
                noOptionsPlaceholder: 'cfm_helptext_no_validations',
            },
            {
                schemaId: 'defaultValue',
                label: 'cfm_label_default_value',
                uiType: 'text',
                placeholder: 'cfm_placeholder_type_value',
                isHidden: true,
            },
            {
                schemaId: 'length',
                label: 'cfm_label_max_characters',
                uiType: 'number',
                placeholder: 'cfm_placeholder_integer',
                isHidden: true,
            },
            {
                schemaId: 'branches',
                label: 'cfm_label_visibility_environments',
                uiType: 'multiList',
                placeholder: 'label_selectone',
                emptyPlaceholder: 'cfm_label_all',
            },

            {
                schemaId: 'dependenceField',
                label: 'cfm_label_parent_dependency',
                uiType: 'list',
                placeholder: 'label_selectone',
            },
            {
                schemaId: 'dependentAlert',
                uiType: 'message',
                placeholder: 'cfm_label_dependant_field_extra_info',
                isHidden: true,
            },
            {
                schemaId: 'relevantFields',
                label: 'cfm_label_featured_by_usertype',
                uiType: 'multiList',
                placeholder: 'label_selectone',
            },
            {
                schemaId: 'generalSettings',
                uiType: 'checkboxGroup',
                uiGrid: 'linearVertical',
                label: 'cfm_label_general_config',
                composition: [
                    {
                        schemaId: 'visible',
                        label: 'cfm_label_visible_all_devices',
                        uiType: 'checkbox',
                        helpTip: null,
                    },
                    {
                        schemaId: 'mobileVisibility',
                        label: 'cfm_label_visible_mobility',
                        uiType: 'checkbox',
                        helpTip: null,
                    },
                    {
                        schemaId: 'visibleInList',
                        label: 'cfm_label_include_in_views',
                        uiType: 'checkbox',
                        helpTip: null,
                    },
                    {
                        schemaId: 'searchable',
                        label: 'cfm_label_include_search',
                        uiType: 'checkbox',
                        helpTip: null,
                    },
                    {
                        schemaId: 'mandatory',
                        label: 'cfm_label_mandatory_only_apps',
                        uiType: 'checkbox',
                        helpTip: null,
                    },
                    {
                        schemaId: 'readOnly',
                        label: 'cfm_label_readonly',
                        uiType: 'checkbox',
                        helpTip: null,
                    },
                    {
                        schemaId: 'fuzzySearch',
                        label: 'cfm_label_is_fuzzy',
                        uiType: 'checkbox',
                        helpTip: null,
                    },
                    {
                        schemaId: 'visibleInPhoneCallForm',
                        label: 'cfm_label_visible_in_phone_call_form',
                        uiType: 'checkbox',
                        helpTip: null,
                    },
                    {
                        schemaId: 'restApi',
                        label: 'cfm_label_visible_restapi_v4',
                        uiType: 'checkbox',
                        helpTip: null,
                    },
                    {
                        schemaId: 'mandatoryRestApi',
                        label: 'cfm_label_mandatory_in_api',
                        uiType: 'checkbox',
                        helpTip: null,
                        isHidden: false,
                    },
                ],
            },
            {
                schemaId: 'comment',
                label: 'cfm_label_internal_comment',
                uiType: 'textarea',
                placeholder: 'cfm_label_internal_comment_desc',
            },
            {
                schemaId: 'integrationSection',
                uiType: 'section',
                label: 'label_cfm_integration_configuration',
                composition: [
                    {
                        schemaId: 'integrationField',
                        label: 'label_integration_direction',
                        uiType: 'list',
                        placeholder: 'label_selectone',
                    },
                    {
                        schemaId: 'integrationFieldName',
                        label: 'cfm_label_integrated_field',
                        uiType: 'text',
                    },
                ],
            },
            {
                schemaId: 'dynamicsSection',
                uiType: 'section',
                label: 'label_sf_config_dynamism',
                composition: [
                    {
                        schemaId: 'dynamicField',
                        label: 'cfm_label_parent_dynamic_name',
                        uiType: 'list',
                        placeholder: 'label_selectone',
                        emptyPlaceholder: 'cfm_placeholder_type_value',
                    },
                    {
                        schemaId: 'dynamicFieldValues',
                        label: 'cfm_label_parent_dynamic_value',
                        uiType: 'multiList',
                        isFuzzy: true,
                        placeholder: 'label_selectone',
                        emptyPlaceholder: 'cfm_label_all',
                    },
                    {
                        schemaId: 'useSmartFields',
                        label: 'label_sf_enable_option',
                        uiType: 'switch',
                        helpTip: 'label_sf_disclaimer',
                        isHidden: true,
                    },
                    {
                        schemaId: 'smartFieldsValuesSection',
                        uiType: 'section',
                        label: 'label_sf_values',
                        isHidden: true,
                        composition: [
                            {
                                schemaId: 'smartFieldsValuesList',
                                uiType: 'custom',
                                isHidden: true,
                            },
                        ],
                    },
                ],
            },
        ],
    },
};

const initialState = {
    data: {},
    originalData: {},
    entities: [],
    crudType: null,
    errors: {},
    isOpen: false,
    isLoading: true,
    isSaving: false,
    valueLists: {},
    tables: {},
    stackIndex: 1,
    validationsCode: {},
    rowIndex: null,
};

const loadedValues = [];

const notAllowEntities = [
    'coferta',
    'tblcampaignsstatuses',
    'tblcampaignstypes',
    'tblsales',
    'tblsurveysvalues',
    'tblsalesorderslines',
    'tblformstemplate',
    'tblsalesorders',
];
const entitiesLiterals = {
    Empresas: 'cfm_label_accounts',
    Contactos: 'cfm_label_contacts',
    Expedientes: 'cfm_label_opportunities',
    Gestiones: 'cfm_label_activities',
    tblAgenda: 'cfm_label_event_task',
    tblSalesOrders: 'cfm_label_salesorders',
    Productos: 'cfm_label_products',
    tblUsuarios: 'cfm_label_users',
};

const allowedForCustomValidations = ['Expedientes', 'Empresas', 'Contactos', 'tblAgenda'];

const allowedTypesOfValidation = ['text', 'int', 'percentage', 'decimal', 'currency', 'datetime'];

const allowedForRelevantFields = ['Empresas', 'Expedientes'];

function reducer(state, action) {
    switch (action.type) {
        case 'init':
            let crudType = CRUD_TYPE_ADD;
            let data;
            if (action.duplicate) {
                crudType = CRUD_TYPE_DUPLICATE;
            } else if (action.data?.id) {
                crudType = CRUD_TYPE_EDIT;
            }

            if (crudType === CRUD_TYPE_ADD) {
                data = {
                    entity: {
                        label: action.entityLabel || action.entity,
                        value: action.entity,
                    },
                    length: '255',
                    tabId: action.valueLists.tabs.find((el) => el.value === -1),
                    visible: true,
                    mobileVisibility: true,
                    visibleInList: true,
                    searchable: true,
                    mandatory: false,
                    readOnly: false,
                    fuzzySearch: false,
                    visibleInPhoneCallForm: false,
                    restApi: true,
                    isEditable: true,
                };
            } else {
                const boolType = action.data.defaultValue === '1';
                const finalDefaultValue =
                    (action.data.type === 'list' ||
                        action.data.type?.toLowerCase() === 'multivalue') &&
                    action.data.defaultValue
                        ? Number(action.data.defaultValue)
                        : action.data.defaultValue;

                const valueListRelated = action.valueLists.valueListRelated?.find(
                    (el) => el.value === action.data.valueListRelated,
                ) || {
                    label: action.data.valueListRelated,
                    value: action.data.valueListRelated,
                };

                data = {
                    ...action.data,
                    id: crudType === CRUD_TYPE_EDIT ? action.data.id : -1,
                    fieldName:
                        crudType === CRUD_TYPE_EDIT
                            ? action.data.fieldName
                            : action.data.fieldName.replace('Z_', '') + '_Copy',
                    entity: {
                        label: action.entity,
                        value: action.entity,
                    },
                    relevantFields: action.data.relevantFields?.map((el) => ({
                        value: el.id,
                        label: el.value,
                    })),
                    type: action.valueLists.type?.find((el) => el.value === action.data.type),
                    defaultValue: action.data.type === 'bit' ? boolType : finalDefaultValue,
                    valueListRelated:
                        action.valueLists.valueListRelated && action.data.valueListRelated
                            ? valueListRelated
                            : null,
                    tabId: action.valueLists.tabs?.find((el) => el.value === action.data.tabId),
                    branches: action.data.branches?.map((el) => ({
                        value: el.id,
                        label: el.value,
                    })),
                    dynamicField: action.valueLists.dynamicField?.find(
                        (el) => el.value === action.data.dynamicField,
                    ),
                    dynamicFieldValues: action.data.dynamicFieldValues?.map((el) => ({
                        value: el.id,
                        label: el.value,
                    })),
                    dependenceField: action.valueLists.dependenceField?.find(
                        (el) => el.value === action.data.dependenceField,
                    ),
                };
            }

            const obj = {
                ...state,
                originalData: data,
                data,
                fields: action.fields,
                entities: action.entities,
                tabs: action.tabs,
                crudType,
                errors: {},
                isOpen: true,
                isLoading: false,
                valueLists: action.valueLists,
                tables: {
                    ...state.tables,
                    dynamicFieldValues: data?.dynamicField?.valueListRelated,
                },
                rowIndex: action.rowIndex || null,
            };
            return obj;
        case 'close':
            return { ...state, isSaving: false, isOpen: false };
        case 'changeEntity':
            return {
                ...state,
                data: {
                    ...state.data,
                    ...action.data,
                    tabId: null,
                    dependenceField: null,
                    dynamicField: null,
                    dynamicFieldValues: null,
                },
                valueLists: {
                    ...state.valueLists,
                    ...action.valueLists,
                },
            };
        case 'changeTabs':
            return {
                ...state,
                valueLists: {
                    ...state.valueLists,
                    tabs: action.tabs,
                },
            };
        case 'changeData':
            return {
                ...state,
                data: {
                    ...state.data,
                    ...action.data,
                    mandatoryRestApi: !action.data.restApi ? false : action.data.mandatoryRestApi,
                },
            };
        case 'changeDataAndTables':
            const finalData = {
                ...state.data,
                ...action.data,
                dynamicFieldValues:
                    action.data?.dynamicField &&
                    action.data?.dynamicField?.valueListRelated ===
                        state.data?.dynamicField?.valueListRelated
                        ? action.data.dynamicFieldValues
                        : undefined,
            };
            const finalObj = {
                ...state,
                data: finalData,
                tables: { ...state.tables, dynamicFieldValues: action.tablesDynamicFieldValues },
            };

            return finalObj;
        case 'changeValueList':
            let label = action.data.name;

            if (action.isWeb) {
                if (action.data.name?.match(/^tbl/)) {
                    label = action.data.name.replace(/^tbl/, '');
                } else if (action.data.name?.match(/^Z_tbl/)) {
                    label = action.data.name.replace(/^Z_tbl/, '');
                }
            } else {
                if (action.data.name?.match(/^tbl/)) {
                    label = `Z_${action.data.name} (${action.data.name.replace(/^tbl/, '')})`;
                } else if (action.data.name?.match(/^Z_tbl/)) {
                    label = `${action.data.name} (${action.data.name.replace(/^Z_tbl/, '')})`;
                }
            }

            loadedValues.push({
                label,
                value: action.data.name,
                isEditable: action.data.isEditable,
                id: action.data.id || action.data.idList,
            });
            state = {
                ...state,
                data: {
                    ...state.data,
                    valueListRelated: {
                        label,
                        value: action.data.name,
                        isEditable: action.data.isEditable,
                        id: action.data.id || action.data.idList,
                    },
                },
                loadedValues,
            };
            return state;
        case 'changeValidation':
            return {
                ...state,
                data: {
                    ...state.data,
                    customValidation: action.data,
                },
            };
        case 'setErrors':
            return { ...state, isSaving: false, errors: action.errors };
        case 'setSaving':
            return {
                ...state,
                isSaving: action.value,
            };
        case 'setValidationCode':
            return {
                ...state,
                validationsCode: { ...state.validationsCode, [action.validation]: action.code },
            };
        case 'updateSmartFieldConfig':
            const existingConfigs = state.data.smartFields || [];
            const filteredConfigs = existingConfigs.filter((c) => c.value !== action.valueId);
            return {
                ...state,
                data: {
                    ...state.data,
                    smartFields: [...filteredConfigs, ...action.configs],
                },
            };
        default:
            throw new Error('No action provided');
    }
}

const FormExtraField = ({
    onRef,
    refreshList,
    fieldsData,
    idSetupRole,
    setActiveRowId,
    processDataBeforeSave,
    entityLogName,
    entityLogSubmodule,
    avoidTours,
    overrides,
    onOpenUpgradeTabsInfo,
    onOpenUpgradeAdminInfo,
    entity,
    getEntityData,
    isSage = false,
}) => {
    const {
        t,
        language,
        permissions,
        logEvent,
        deviceCache,
        setDeviceCache,
        overridePermissions,
        customGeneralTab,
        overrideTours,
        customOnCloseTour,
        fieldsToHide = [],
        fieldsToAvoidExpand = [],
        hideCreateValidation,
        tracking,
        hideSaveAndCreateAnother,
        isWeb,
        canCreateTabs,
        onClickGoToAdmin,
    } = useWeb5Context({ overrides });
    const dumbUser = idSetupRole ? [8, 9].includes(idSetupRole) : false;
    const [state, dispatch] = useReducer(reducer, initialState);
    const schema = useRef(JSON.parse(JSON.stringify(FORM_SCHEMA)));
    const formValueLists = useRef();
    const formValidations = useRef();
    const formTab = useRef();
    const formSmartFieldConfig = useRef();
    const [stackIndex, setStackIndex] = useState(1);

    const classes = useStyles();

    // SmartFields hooks
    const {
        valueListItems,
        hasMore,
        loadMore,
        isLoading: isLoadingValues,
    } = useSmartFieldsValues({
        valueListRelated: state.data?.dynamicField?.valueListRelated,
        language,
        useSmartFields: state.data?.useSmartFields,
    });

    const {
        showConfirmModal: showSmartFieldsConfirmModal,
        handleToggle: handleToggleSmartFields,
        handleConfirm: handleConfirmSmartFields,
        handleCancelConfirm: handleCancelSmartFieldsConfirm,
    } = useSmartFieldsToggle({
        currentData: state.data,
        onToggleSmartFields: (data) => dispatch({ type: 'changeData', data }),
    });

    // Child method
    useEffect(() => {
        onRef &&
            onRef({
                open({ id, entities, entity, duplicate, rowIndex }) {
                    const fields = fieldsData[entity].fields;
                    const getExtraFieldData = (id) => {
                        if (id) {
                            return getExtraField(id);
                        }
                        return Promise.resolve({ data: null });
                    };
                    Promise.all([
                        getParameter('implementation', 'CheckDuplicateFields'),
                        getExtraFieldData(id),
                        getList({ list: 'tblIntegrationValues' }),
                    ])
                        .then(([parameter, { data }, integrationValuesResponse]) => {
                            // Checking duplicate param is disabled
                            const duplicateParamDisabled =
                                parameter?.data?.value === '0' || !parameter?.data?.value;

                            const relatedOptions = entities
                                ?.filter(
                                    (f) => notAllowEntities.indexOf(f.name?.toLowerCase()) === -1,
                                )
                                .map((efEntity) => {
                                    let { name: value } = efEntity;
                                    const customLiteral =
                                        t(entitiesLiterals[efEntity.name]) || null;
                                    value = value === 'Contactos' ? 'viewContactosFullName' : value;
                                    value =
                                        value === 'tblUsuarios' ? 'viewUsuariosFullName' : value;
                                    return {
                                        label: getDescription({
                                            tab: efEntity,
                                            dumbUser,
                                            customLiteral,
                                            isWeb,
                                        }),
                                        value,
                                        id: efEntity?.id,
                                        isEditable: efEntity?.isEditable,
                                    };
                                });

                            const allDynamicFields = formatDynamicFields(
                                data?.fieldName || '',
                                data?.entity?.value || entity,
                                fields,
                                language,
                                t,
                                fieldsData,
                            );

                            const integrationFieldOptions =
                                integrationValuesResponse?.data?.map((item) => ({
                                    value: item.id,
                                    label: item.description,
                                })) || [];

                            const valueLists = {
                                entity: entities?.map((option) => {
                                    const customLiteral = t(entitiesLiterals[option.name]) || null;
                                    return {
                                        label: getDescription({
                                            tab: option,
                                            dumbUser,
                                            customLiteral,
                                            isWeb,
                                        }),
                                        value: option.name,
                                    };
                                }),
                                type: getFieldTypesList(t),
                                valueListRelated: relatedOptions,
                                tabs: getTabsOptions(fields?.tab, t, customGeneralTab),
                                dynamicField: allDynamicFields.filter(
                                    (field) => field.value !== data?.fieldName,
                                ),
                                dynamicFieldValues: null,
                                dependenceField: formatDependenceFields(
                                    data?.fieldName || '',
                                    fields,
                                    language,
                                    t,
                                ),
                                integrationField: integrationFieldOptions,
                            };
                            const layoutComposition = schema.current.layout.composition.filter(
                                (item) =>
                                    ![
                                        'generalSettings',
                                        'checkDuplicatesSection',
                                        'comment',
                                        'integrationSection',
                                        'dynamicsSection',
                                    ].includes(item.schemaId),
                            );

                            const generalSettings = schema.current.layout.composition.find(
                                (item) => item.schemaId === 'generalSettings',
                            );

                            const internalComment = schema.current.layout.composition.find(
                                (item) => item.schemaId === 'comment',
                            );

                            const integrationSection = schema.current.layout.composition.find(
                                (item) => item.schemaId === 'integrationSection',
                            );

                            // SmartFields sections (dynamicsSection contains all nested fields)
                            const dynamicsSection = schema.current.layout.composition.find(
                                (item) => item.schemaId === 'dynamicsSection',
                            );

                            const generalSettingsComposition = [...generalSettings.composition];

                            const mandatoryIndex = generalSettingsComposition.findIndex(
                                (item) => item.id === 'mandatory',
                            );
                            const readOnlyIndex = generalSettingsComposition.findIndex(
                                (item) => item.id === 'readOnly',
                            );
                            const visibleInPhoneCallFormIndex = {
                                ...generalSettingsComposition.find(
                                    (item) => item.id === 'visibleInPhoneCallForm',
                                ),
                            };
                            const mandatoryRestApiIndex = generalSettingsComposition.find(
                                (item) => item.id === 'mandatoryRestApi',
                            );

                            generalSettingsComposition[mandatoryIndex] = {
                                ...generalSettingsComposition[mandatoryIndex],
                                isDisabled: data && !data.visible && !data.mobileVisibility,
                            };
                            generalSettingsComposition[readOnlyIndex] = {
                                ...generalSettingsComposition[readOnlyIndex],
                                isDisabled: data && !data.visible && !data.mobileVisibility,
                            };
                            generalSettingsComposition[visibleInPhoneCallFormIndex] = {
                                ...generalSettingsComposition[visibleInPhoneCallFormIndex],
                                isDisabled: data && !data.visible && !data.mobileVisibility,
                            };
                            generalSettingsComposition[mandatoryRestApiIndex] = {
                                ...generalSettingsComposition[mandatoryRestApiIndex],
                                isHidden: !data?.restApi,
                            };

                            const newSchema = {
                                ...schema.current,
                                layout: {
                                    ...schema.current.layout,
                                    composition: [
                                        ...layoutComposition,
                                        {
                                            ...generalSettings,
                                            composition: generalSettingsComposition,
                                        },
                                        {
                                            ...internalComment,
                                            isHidden: overridePermissions,
                                        },
                                        integrationSection,
                                    ],
                                },
                            };

                            // Duplicates param funcionality
                            newSchema.layout.composition.push({
                                ...(duplicateParamDisabled
                                    ? DUPLICATES_SECTION_SCHEMA_DISABLED
                                    : DUPLICATES_SECTION_SCHEMA),
                            });

                            // Add SmartFields sections at the end
                            newSchema.layout.composition.push(dynamicsSection);

                            if (fieldsToAvoidExpand?.length) {
                                newSchema.layout.composition?.forEach((field) => {
                                    if (fieldsToAvoidExpand.includes(field.schemaId)) {
                                        field.uiType = 'text';
                                    }

                                    return field;
                                });
                            }
                            schema.current = newSchema;
                            const finalData = {
                                ...data,
                                customValidation: data?.customValidation
                                    ? {
                                          value: data.customValidation.id,
                                          label: data.customValidation.value,
                                      }
                                    : null,
                                useSmartFields: data?.smartFields?.length > 0,
                                integrationField: integrationFieldOptions?.find(
                                    (el) => el.value === data?.integrationField,
                                ),
                                integrationFieldName: data?.integrationFieldName,
                            };

                            dispatch({
                                type: 'init',
                                data: finalData,
                                entities,
                                fields,
                                entity,
                                entityLabel: t(entitiesLiterals[entity]) || entity,
                                duplicate,
                                valueLists,
                                rowIndex,
                            });
                        })
                        .catch((error) => {
                            console.error(error);
                            showErrorToast(t, error);
                            dispatch({ type: 'setErrors', errors: error });
                        });
                },
            });
    }, [
        fieldsData,
        language,
        onRef,
        state.data,
        state.entities,
        state.fields,
        t,
        dumbUser,
        customGeneralTab,
        overridePermissions,
        fieldsToHide,
        fieldsToAvoidExpand,
        isWeb,
    ]);

    const validateDefaultValue = useCallback(
        ({ isValid, errors, cleanData }) => {
            const validation = cleanData.customValidation?.value || null;
            const getValidationCode = () => {
                if (!validation) return Promise.resolve({ code: null });
                if (state.validationsCode[validation])
                    return Promise.resolve({ code: state.validationsCode[validation] });
                return getFieldValidation(validation);
            };

            return getValidationCode()
                .then(({ code }) => {
                    if (!code) return null;
                    if (validation && !state.validationsCode[validation])
                        dispatch({
                            type: 'setValidationCode',
                            validation,
                            code,
                        });
                    return executeSandbox(cleanData.defaultValue, code);
                })
                .then((error) => ({
                    isValid: isValid && !error?.length,
                    errors: {
                        ...errors,
                        defaultValue: error?.length ? { message: error } : errors.defaultValue,
                    },
                    cleanData,
                }));
        },
        [state.validationsCode],
    );

    const onSave = useCallback(() => {
        const useSmartFields = state.data?.useSmartFields;
        const required =
            state.data?.dynamicField && !useSmartFields
                ? [...schema.current?.required, 'dynamicFieldValues']
                : schema.current?.required;
        const finalForm = {
            ...schema.current,
            required,
        };

        const finalData = {
            ...state.data,
            relevantFields: state.data?.relevantFields?.length
                ? state.data.relevantFields.map((el) => el.value)
                : null,
            userstype: state.data?.userstype?.length
                ? state.data.userstype.map((el) => el.id)
                : null,
        };

        validate(finalData, finalForm)
            .then(validateDefaultValue)
            .then(({ isValid, errors, cleanData }) => {
                if (!isValid) {
                    dispatch({ type: 'setErrors', errors });
                } else {
                    dispatch({ type: 'setSaving', value: true });
                    // Tracking 'Link validation'
                    if (
                        !state.originalData.customValidation &&
                        cleanData.customValidation &&
                        (entityLogSubmodule === 'accountsTab' ||
                            entityLogSubmodule === 'contactsTab' ||
                            entityLogSubmodule === 'opportunitiesTab' ||
                            entityLogSubmodule === 'eventsAndTasksTab')
                    )
                        logEvent({
                            event: entityLogName,
                            submodule: entityLogSubmodule,
                            functionality: 'linkValidation',
                        });

                    // Tracking 'Save'
                    logEvent({
                        event: entityLogName,
                        submodule: entityLogSubmodule,
                        functionality: state.crudType === CRUD_TYPE_EDIT ? 'update' : 'create',
                    });

                    let finalCleanData = flatList(cleanData, schema.current);
                    let boolType = finalCleanData.defaultValue ? '1' : '0';
                    let finalDefaultValue =
                        finalCleanData.type === 'bit' ? boolType : `${finalCleanData.defaultValue}`;

                    finalCleanData = {
                        ...finalCleanData,
                        defaultValue: finalCleanData.defaultValue ? finalDefaultValue : '',
                        smartFields: state.data.smartFields || null,
                    };

                    if (processDataBeforeSave) {
                        finalCleanData = processDataBeforeSave(finalCleanData);
                    }

                    if (state.crudType === CRUD_TYPE_EDIT) {
                        updateExtraField(state.data.id, finalCleanData)
                            .then(() => {
                                refreshList && refreshList();
                                setActiveRowId && setActiveRowId(null);
                                dispatch({ type: 'close' });
                                successToast({
                                    t,
                                    text: t(
                                        state.data.isNew
                                            ? 'cfm_success_create_field'
                                            : 'cfm_success_update_field',
                                    ),
                                });
                            })
                            .catch((error) => {
                                console.error(error);
                                showErrorToast(t, error, null, state?.data?.entity.value);
                                dispatch({ type: 'setErrors', errors: error });
                            });
                    } else {
                        createExtraField(finalCleanData)
                            .then((response) => {
                                refreshList && refreshList({ response, rowIndex: state.rowIndex });
                                setActiveRowId && setActiveRowId(null);
                                dispatch({ type: 'close' });
                                successToast({
                                    t,
                                    text: t(
                                        state.data.isNew
                                            ? 'cfm_success_create_field'
                                            : 'cfm_success_update_field',
                                    ),
                                });
                            })
                            .catch((error) => {
                                console.error(error);
                                showErrorToast(t, error, null, state?.data?.entity.value);
                                dispatch({ type: 'setErrors', errors: error });
                            });
                    }
                }
            });
    }, [
        state.data,
        state.originalData.customValidation,
        state.crudType,
        validateDefaultValue,
        entityLogSubmodule,
        logEvent,
        entityLogName,
        processDataBeforeSave,
        refreshList,
        setActiveRowId,
        t,
        state.rowIndex,
    ]);

    const onSaveCreate = useCallback(() => {
        const useSmartFields = state.data?.useSmartFields;
        const required =
            state.data?.dynamicField && !useSmartFields
                ? [...schema.current?.required, 'dynamicFieldValues']
                : schema.current?.required;
        const finalForm = {
            ...schema.current,
            required,
        };

        validate(state?.data, finalForm)
            .then(validateDefaultValue)
            .then(({ isValid, errors, cleanData }) => {
                if (!isValid) {
                    dispatch({ type: 'setErrors', errors });
                } else {
                    dispatch({ type: 'setSaving', value: true });
                    let finalCleanData = flatList(cleanData, schema.current);
                    let boolType = finalCleanData.defaultValue ? '1' : '0';
                    let finalDefaultValue =
                        finalCleanData.type === 'bit' ? boolType : `${finalCleanData.defaultValue}`;

                    finalCleanData = {
                        ...finalCleanData,
                        defaultValue: finalCleanData.defaultValue ? finalDefaultValue : null,
                        smartFields: state.data.smartFields || null,
                    };

                    createExtraField(finalCleanData)
                        .then(() => {
                            refreshList && refreshList();
                            setActiveRowId && setActiveRowId(null);
                            dispatch({
                                type: 'setSaving',
                                value: false,
                            });
                            dispatch({
                                type: 'init',
                                data: {},
                                entities: state.entities,
                                fields: state.fields,
                                entity: state.originalData.entity.value,
                                entityLabel: state.originalData.entity.label,
                                valueLists: state.valueLists,
                            });
                            successToast({
                                t,
                                text: t(
                                    state.data.isNew
                                        ? 'cfm_success_create_field'
                                        : 'cfm_success_update_field',
                                ),
                            });
                        })
                        .catch((error) => {
                            console.error(error);
                            showErrorToast(t, error, null, state?.data?.entity.value);
                            dispatch({ type: 'setErrors', errors: error });
                        });
                }
            });
    }, [
        state.data,
        state.entities,
        state.fields,
        state.originalData.entity,
        state.valueLists,
        validateDefaultValue,
        refreshList,
        setActiveRowId,
        t,
    ]);

    const locales = useMemo(() => {
        let title;
        switch (state.crudType) {
            case CRUD_TYPE_ADD:
                title = t('cfm_title_create_extra_field');
                break;
            case CRUD_TYPE_EDIT:
                title = t('cfm_title_edit_extra_field');
                break;
            case CRUD_TYPE_DUPLICATE:
                title = t('cfm_title_duplicate_extra_field');
                break;
            default:
                break;
        }
        return {
            title,
            cancel: t('cfm_action_cancel'),
            save: t('cfm_action_save'),
            saveAndCreate: t('cfm_action_add_not_close'),
        };
    }, [state.crudType, t]);

    const onClickButtonList = useCallback(
        (value) => {
            setStackIndex(stackIndex + 1);
            formValueLists.current.open({
                id: value.id,
                isEditable: value.isEditable,
            });
            tracking?.updateValueList && tracking.updateValueList();
        },
        [stackIndex, tracking],
    );

    const valueListRelatedActions = useMemo(
        () => [
            {
                label: t('cfm_label_create_table'),
                onClick: () => {
                    validate(state.data, { ...schema.current, required: ['description'] }).then(
                        ({ isValid, errors }) => {
                            if (!isValid) {
                                dispatch({ type: 'setErrors', errors });
                                return;
                            }

                            setStackIndex(stackIndex + 1);
                            formValueLists.current.open({});
                            tracking?.createValueList && tracking.createValueList();
                        },
                    );
                },
            },
        ],
        [stackIndex, t, tracking, state.data],
    );

    const onCreateFieldsTab = useCallback(
        (entities) => {
            formTab.current.open({ entities, entity });
        },
        [entity],
    );

    const tabIdActions = useMemo(
        () => [
            {
                label: t('cfm_action_create_tab'),
                onClick: () => {
                    if (isWeb && !canCreateTabs) onOpenUpgradeTabsInfo();
                    else {
                        setStackIndex(stackIndex + 1);
                        onCreateFieldsTab();
                    }
                },
            },
        ],
        [t, onOpenUpgradeTabsInfo, isWeb, canCreateTabs, onCreateFieldsTab, stackIndex],
    );

    const customValidationsActions = useMemo(
        () => [
            {
                label: t('cfm_action_create_validation'),
                onClick: () => {
                    setStackIndex(stackIndex + 1);
                    formValidations.current.open({ type: state.data.type?.value || null });
                },
            },
        ],
        [stackIndex, state.data, t],
    );

    const customOption = useCallback(
        (data) => {
            return (
                <div className={classes?.typeOptions}>
                    <Icon className={classes?.icon} name={FIELD_TYPE[data.value]} />
                    <span>{data.label}</span>
                </div>
            );
        },
        [classes],
    );

    const stateDataRef = useRef();
    stateDataRef.current = state.data;

    const handleSaveSmartFieldConfig = useCallback((valueId, configs) => {
        dispatch({ type: 'updateSmartFieldConfig', valueId, configs });
    }, []);

    const handleConfigureValue = useCallback(
        (valueId) => {
            setStackIndex((prev) => prev + 1);
            const valueName =
                valueListItems.find((item) => item.value === valueId)?.label || String(valueId);
            formSmartFieldConfig.current.open({
                valueId,
                valueName,
                fieldType: stateDataRef.current?.type?.value,
                fieldConfig: stateDataRef.current,
                existingConfigs: stateDataRef.current?.smartFields || [],
            });
        },
        [valueListItems],
    );

    const renderSmartFieldsValuesList = useMemo(
        () => (
            <SmartFieldsValuesSection
                valueListItems={valueListItems}
                onConfigureValue={handleConfigureValue}
                hasMore={hasMore}
                onLoadMore={loadMore}
                isLoading={isLoadingValues}
                valueListRelated={state.data?.dynamicField?.valueListRelated}
            />
        ),
        [
            valueListItems,
            handleConfigureValue,
            hasMore,
            loadMore,
            isLoadingValues,
            state.data?.dynamicField?.valueListRelated,
        ],
    );

    const onSearchCustomValidation = useCallback(
        (search = '') =>
            getFieldsValidations({
                page: 0,
                name: search,
                fieldType: state.data.type?.value,
                count: 999999999,
            }).then(({ data }) => data.map((el) => ({ value: el.id, label: el.name }))),
        [state.data.type],
    );

    const filterValues = useCallback((values) => {
        let entities = Object.keys(entitiesLiterals);
        entities = [...entities, 'viewContactosFullName', 'viewUsuariosFullName', 'productos'];
        return values?.filter((el) => !entities.includes(el.value));
    }, []);

    const onSearchValues = useCallback(
        (text = 'a') => {
            return searchValues({
                entity: state?.tables?.dynamicFieldValues,
                text,
                lang: language,
            })
                .then(({ data }) => {
                    return data.map((item) => ({
                        label: item.description,
                        value: item.id,
                    }));
                })
                .catch((err) => {
                    console.error(err);
                });
        },
        [language, state.tables.dynamicFieldValues],
    );

    const modifyFieldProps = useCallback(
        (field) => {
            let newField = field;
            let newsSchema = schema.current;

            const overrideIsHidden = (id) =>
                Array.isArray(fieldsToHide) && fieldsToHide.includes(id);

            switch (field.schemaId || field.id) {
                case 'fieldName':
                    newField = {
                        ...newField,
                        hint: t('cfm_label_fieldname_api_name_help'),
                        isReadOnly: state.crudType === CRUD_TYPE_EDIT,
                        isHidden: overrideIsHidden('fieldName'),
                    };
                    break;
                case 'entity':
                    newField = {
                        ...newField,
                        options: state.valueLists.entity,
                        isReadOnly: state.crudType === CRUD_TYPE_EDIT,
                        isHidden: overrideIsHidden('entity'),
                    };
                    break;
                case 'type':
                    newField = {
                        ...newField,
                        options: state.valueLists.type,
                        overrides: {
                            'react-select': {
                                formatOptionLabel: customOption,
                            },
                        },
                        isReadOnly: state.crudType === CRUD_TYPE_EDIT,
                    };

                    const fieldsBySchemaId = newsSchema.layout.composition.reduce((acc, item) => {
                        if (item.schemaId) acc[item.schemaId] = item;
                        return acc;
                    }, {});

                    const {
                        includeTime: includeTimeField,
                        valueListRelated: valueListRelatedField,
                        defaultValue: defaultValueField,
                        length: lengthField,
                    } = fieldsBySchemaId;
                    const configByType = {
                        datetime: () => {
                            defaultValueField.uiType = 'text';
                            includeTimeField.isHidden = false;
                            defaultValueField.isHidden = true;
                            lengthField.isHidden = true;
                            valueListRelatedField.isHidden = true;
                        },
                        bit: () => {
                            defaultValueField.uiType = 'checkbox';
                            includeTimeField.isHidden = true;
                            defaultValueField.isHidden = false;
                            lengthField.isHidden = true;
                            valueListRelatedField.isHidden = true;
                        },
                        int: () => {
                            defaultValueField.uiType = 'integer';
                            defaultValueField.placeholder = 'cfm_placeholder_numeric';
                            includeTimeField.isHidden = true;
                            defaultValueField.isHidden = false;
                            lengthField.isHidden = true;
                            valueListRelatedField.isHidden = true;
                        },
                        multivalue: () => {
                            defaultValueField.placeholder = 'label_selectone';
                            defaultValueField.uiType = 'list';
                            includeTimeField.isHidden = true;
                            defaultValueField.valueObjectNeeded =
                                state.data?.valueListRelated?.value;
                            defaultValueField.isHidden = false;
                            lengthField.isHidden = true;
                            valueListRelatedField.isHidden = false;
                        },
                        list: () => {
                            defaultValueField.placeholder = 'label_selectone';
                            defaultValueField.uiType = 'list';
                            includeTimeField.isHidden = true;
                            defaultValueField.valueObjectNeeded =
                                state.data?.valueListRelated?.value;
                            defaultValueField.isHidden = false;
                            lengthField.isHidden = true;
                            valueListRelatedField.isHidden = false;
                        },
                        percentage: () => {
                            defaultValueField.uiType = 'number';
                            defaultValueField.placeholder = 'cfm_placeholder_percentage';
                            includeTimeField.isHidden = true;
                            defaultValueField.isHidden = false;
                            lengthField.isHidden = true;
                            valueListRelatedField.isHidden = true;
                        },
                        decimal: () => {
                            defaultValueField.uiType = 'number';
                            defaultValueField.placeholder = 'cfm_placeholder_decimal';
                            includeTimeField.isHidden = true;
                            defaultValueField.isHidden = false;
                            lengthField.isHidden = true;
                            valueListRelatedField.isHidden = true;
                        },
                        currency: () => {
                            defaultValueField.uiType = 'number';
                            defaultValueField.placeholder = 'cfm_placeholder_percentage';
                            includeTimeField.isHidden = true;
                            defaultValueField.isHidden = false;
                            lengthField.isHidden = true;
                            valueListRelatedField.isHidden = true;
                        },
                        text: () => {
                            defaultValueField.uiType = 'text';
                            defaultValueField.placeholder = 'cfm_placeholder_type_value';
                            includeTimeField.isHidden = true;
                            defaultValueField.isHidden = false;
                            lengthField.isHidden = false;
                            valueListRelatedField.isHidden = true;
                        },
                        default: () => {
                            includeTimeField.isHidden = true;
                            defaultValueField.isHidden = true;
                            lengthField.isHidden = true;
                        },
                        withoutType: () => {
                            includeTimeField.isHidden = true;
                            valueListRelatedField.isHidden = true;
                            defaultValueField.isHidden = true;
                        },
                    };

                    if (!state.data?.type?.value) {
                        configByType.withoutType();
                    } else {
                        configByType[state.data.type.value]?.() || configByType.default?.();
                    }
                    break;
                case 'includeTime':
                    newField = {
                        ...newField,
                        isHidden: state.data?.type?.value !== 'datetime',
                    };
                    break;
                case 'defaultValue':
                    newField = {
                        ...newField,
                        hint: t('cfm_label_default_value_help'),
                        key: state.data?.valueListRelated?.value,
                        isHidden: overrideIsHidden('defaultValue'),
                    };
                    break;
                case 'valueListRelated':
                    const { loadedValues } = state;
                    newField = {
                        ...newField,
                        options:
                            state.data?.type?.value === 'multivalue'
                                ? filterValues(state.valueLists.valueListRelated)
                                : state.valueLists.valueListRelated,
                        loadedValues,
                    };
                    if (overridePermissions || permissions?.valueList?.create) {
                        newField = {
                            ...newField,
                            actions: valueListRelatedActions,
                        };
                    }
                    if (overridePermissions || permissions?.valueList?.update) {
                        newField = {
                            ...newField,
                            onClickButtonList,
                        };
                    }
                    newsSchema.context = {
                        ...newsSchema.context,
                        fields: {
                            ...newsSchema.context.fields,
                            defaultValue: { table: state.data?.valueListRelated?.value },
                        },
                    };
                    break;
                case 'customValidation':
                    newField = {
                        ...newField,
                        actions:
                            !hideCreateValidation &&
                            permissions?.fieldsValidations?.create &&
                            customValidationsActions,
                        loadOptionsWithFuzzy: onSearchCustomValidation,
                        isFuzzy: true,
                        overrides: {
                            'react-select': {
                                defaultOptions: true,
                                openMenuOnClick: true,
                            },
                        },
                        isHidden:
                            overrideIsHidden('customValidation') ||
                            !state.data?.type?.value ||
                            !allowedTypesOfValidation.includes(state.data?.type?.value) ||
                            (!overridePermissions &&
                                !permissions?.fieldsValidations?.rootFieldsValidations) ||
                            !allowedForCustomValidations.includes(state.data?.entity?.value),
                    };
                    break;
                case 'relevantFields':
                    newField = {
                        ...newField,
                        isHidden:
                            overrideIsHidden('relevantFields') ||
                            !allowedForRelevantFields.includes(state.data?.entity?.value),
                    };
                    break;
                case 'tabId':
                    newField = {
                        ...newField,
                        actions: tabIdActions,
                        options: state.valueLists.tabs,
                    };
                    break;
                case 'description':
                    newField = {
                        ...newField,
                        hint: t('cfm_label_field_name_help'),
                    };
                    break;
                case 'isEditable':
                    newField = {
                        ...newField,
                        hint: t('cfm_field_user_editable_help'),
                        isHidden: !permissions?.extraFields?.userUpdate || overridePermissions,
                    };
                    break;
                case 'branches':
                    newField = {
                        ...newField,
                        isHidden: overrideIsHidden('branches'),
                        hint: t('cfm_label_visibility_environments_help'),
                    };
                    break;
                case 'dynamicsSection':
                    newField = {
                        ...newField,
                        isHidden: overrideIsHidden('dynamicsSection'),
                    };
                    break;
                case 'dynamicField':
                    newField = {
                        ...newField,
                        options: state.valueLists.dynamicField,
                        isReadOnly: !state.data?.entity,
                        hint: t('cfm_label_tooltip_dynamic_fields'),
                        isHidden: overrideIsHidden('dynamicField'),
                    };
                    break;
                case 'integrationSection':
                    newField = {
                        ...newField,
                        isHidden: !isSage,
                    };
                    break;
                case 'integrationField':
                    newField = {
                        ...newField,
                        options: state.valueLists?.integrationField || [],
                        isReadOnly: !permissions?.extraFields?.updateIntegrationField,
                    };
                    break;
                case 'integrationFieldName':
                    newField = {
                        ...newField,
                        isReadOnly: !permissions?.extraFields?.updateIntegrationField,
                    };
                    break;
                case 'useSmartFields':
                    const isDynamicFieldMultivalue =
                        state.data?.dynamicField?.type === 'multivalue';
                    newField = {
                        ...newField,
                        isHidden: overrideIsHidden('useSmartFields'),
                        isReadOnly: !state.data?.dynamicField?.value || isDynamicFieldMultivalue,
                    };
                    break;
                case 'smartFieldsValuesSection':
                    newField = {
                        ...newField,
                        isHidden:
                            overrideIsHidden('smartFieldsValuesSection') ||
                            !state.data?.useSmartFields ||
                            !state.data?.dynamicField?.value,
                    };
                    break;
                case 'smartFieldsValuesList':
                    newField = {
                        ...newField,
                        isHidden:
                            overrideIsHidden('smartFieldsValuesList') ||
                            !state.data?.useSmartFields ||
                            !state.data?.dynamicField?.value,
                        Field: () => renderSmartFieldsValuesList,
                    };
                    break;
                case 'dynamicFieldValues':
                    const hasSmartFields =
                        !!state.data?.smartFields && state.data.smartFields.length > 0;
                    const useSmartFields = state.data?.useSmartFields;
                    newField = {
                        ...newField,
                        isReadOnly:
                            !state.data?.entity ||
                            !state.data?.dynamicField ||
                            hasSmartFields ||
                            useSmartFields,
                        isRequired: state.data?.dynamicField && !useSmartFields,
                        isDisabled: hasSmartFields || useSmartFields,
                        table: state.tables.dynamicFieldValues,
                        loadOptionsWithFuzzy: onSearchValues,
                        key: state.tables.dynamicFieldValues,
                        isHidden: overrideIsHidden('dynamicFieldValues'),
                    };
                    break;
                case 'dependenceField':
                    newField = {
                        ...newField,
                        options: state.valueLists.dependenceField,
                        hint: t('cfm_label_tooltip_dependant_fields'),
                        isHidden: overrideIsHidden('dependenceField'),
                        isReadOnly:
                            state.data?.type?.value !== 'list' &&
                            state.data?.type?.value !== 'multivalue',
                    };
                    newsSchema.layout.composition.forEach((item) => {
                        if (['dependentAlert'].includes(item.schemaId)) {
                            if (state.data.dependenceField) item.isHidden = false;
                            else item.isHidden = true;
                        }
                    });
                    break;
                case 'checkDuplicatesSection':
                    newField = {
                        ...newField,
                        isHidden:
                            overrideIsHidden('checkDuplicatesSection') ||
                            !(
                                ENABLED_DUPLICATE_ENTITIES.includes(state?.data?.entity?.value) &&
                                state.data?.type?.value === 'text'
                            ),
                        className: 'fs-form__section-checkDuplicates',
                    };
                    break;
                case 'generalSettings':
                    newField = fieldsToHide.length
                        ? {
                              ...newField,
                              inputs: newField.inputs.map((input) =>
                                  overrideIsHidden(input.name)
                                      ? { ...input, isHidden: true }
                                      : input,
                              ),
                          }
                        : newField;
                    break;
                case 'length': {
                    newField = {
                        ...newField,
                        isHidden: overrideIsHidden('length'),
                    };
                    break;
                }
                case 'literalKey': {
                    newField = {
                        ...newField,
                        isHidden: true || overrideIsHidden('literalKey'),
                    };
                    break;
                }
                default:
                    break;
            }

            schema.current = newsSchema;
            return newField;
        },
        [
            fieldsToHide,
            t,
            state,
            customOption,
            filterValues,
            overridePermissions,
            permissions?.valueList?.create,
            permissions?.valueList?.update,
            permissions?.fieldsValidations?.create,
            permissions?.fieldsValidations?.rootFieldsValidations,
            permissions?.extraFields?.userUpdate,
            permissions?.extraFields?.updateIntegrationField,
            hideCreateValidation,
            customValidationsActions,
            onSearchCustomValidation,
            onSearchValues,
            valueListRelatedActions,
            onClickButtonList,
            tabIdActions,
            renderSmartFieldsValuesList,
            isSage,
        ],
    );

    const onSaveValueListForm = useCallback(
        (data) => {
            dispatch({ type: 'changeValueList', data, isWeb, dumbUser });
        },
        [isWeb, dumbUser],
    );

    const onSaveFieldValidationForm = useCallback((data) => {
        dispatch({ type: 'changeValidation', data: { value: data.id, label: data.name } });
    }, []);

    const onCancel = useCallback(() => {
        setActiveRowId && setActiveRowId(null);
        dispatch({ type: 'close' });
    }, [setActiveRowId]);

    const onChange = useCallback(
        (data, field) => {
            let finalData;
            switch (field) {
                case 'description':
                    if (
                        fieldsToHide.includes('fieldName') &&
                        (state.crudType === CRUD_TYPE_ADD || state.crudType === CRUD_TYPE_DUPLICATE)
                    ) {
                        finalData = {
                            ...data,
                            fieldName: normalizeFieldName(data.description),
                        };
                    } else {
                        finalData = data;
                    }
                    dispatch({ type: 'changeData', data: finalData });
                    break;
                case 'type':
                    finalData = {
                        ...data,
                        defaultValue: null,
                        valueListRelated: null,
                        customValidation: null,
                        dependenceField:
                            data.type?.value === 'list' || data.type?.value === 'multivalue'
                                ? data.dependenceField
                                : null,
                        includeTime: data.type?.value === 'datetime',
                    };
                    dispatch({ type: 'changeData', data: finalData });
                    break;
                case 'entity':
                    if (data.entity)
                        getTabs(data.entity.value).then(({ data }) => {
                            dispatch({
                                type: 'changeTabs',
                                tabs: getTabsOptions(data, t, customGeneralTab),
                            });
                        });

                    const allDynamicFields = formatDynamicFields(
                        state.data?.fieldName,
                        state.data?.entity?.value,
                        state.fields,
                        language,
                        t,
                        fieldsData,
                    );

                    const valueLists = {
                        dynamicField: allDynamicFields.filter(
                            (field) => field.value !== state.data?.fieldName,
                        ),
                    };
                    dispatch({ type: 'changeEntity', data, valueLists });
                    break;
                case 'length':
                    finalData = { ...data };
                    if (data.length > 4000) finalData.length = 4000;
                    dispatch({ type: 'changeData', data: finalData });
                    break;
                case 'valueListRelated':
                    schema.current.context = {
                        ...schema.current.context,
                        fields: {
                            ...schema.current.context.fields,
                            defaultValue: {
                                table: data?.valueListRelated?.label || null,
                            },
                        },
                    };

                    finalData = {
                        ...data,
                        fuzzySearch: isEntityList(data?.valueListRelated?.label),
                        defaultValue: null,
                    };
                    dispatch({ type: 'changeData', data: finalData });
                    break;
                case 'dynamicField':
                    const tablesDynamicFieldValues = data?.dynamicField?.valueListRelated;
                    const isNewDynamicFieldMultivalue = data?.dynamicField?.type === 'multivalue';
                    const isDynamicFieldCleared = !data?.dynamicField;
                    schema.current.context = {
                        ...schema.current.context,
                        fields: {
                            ...schema.current.context.fields,
                            dynamicFieldValues: { table: data?.dynamicField?.valueListRelated },
                        },
                    };
                    const dynamicFieldData =
                        isNewDynamicFieldMultivalue || isDynamicFieldCleared
                            ? {
                                  ...data,
                                  useSmartFields: false,
                                  smartFields: [],
                              }
                            : data;
                    dispatch({
                        type: 'changeDataAndTables',
                        data: dynamicFieldData,
                        tablesDynamicFieldValues,
                    });
                    break;
                case 'useSmartFields':
                    handleToggleSmartFields(data.useSmartFields);
                    break;
                case 'dynamicFieldValues':
                    finalData = {
                        ...data,
                        dynamicFieldValues: data?.dynamicFieldValues.length
                            ? data?.dynamicFieldValues
                            : undefined,
                    };
                    dispatch({ type: 'changeData', data: finalData });
                    break;
                case 'generalSettings':
                    const generalSettings = schema.current.layout.composition.find(
                        (item) => item.schemaId === 'generalSettings',
                    );
                    const generalSettingsComposition = [...generalSettings.composition];

                    const mandatoryIndex = generalSettingsComposition.findIndex(
                        (item) => item.id === 'mandatory',
                    );
                    const readOnlyIndex = generalSettingsComposition.findIndex(
                        (item) => item.id === 'readOnly',
                    );
                    const visibleInPhoneCallFormIndex = {
                        ...generalSettingsComposition.find(
                            (item) => item.id === 'visibleInPhoneCallForm',
                        ),
                    };
                    const mandatoryRestApiIndex = generalSettingsComposition.find(
                        (item) => item.id === 'mandatoryRestApi',
                    );

                    generalSettingsComposition[mandatoryIndex] = {
                        ...generalSettingsComposition[mandatoryIndex],
                        isDisabled: data && !data.visible && !data.mobileVisibility,
                    };
                    generalSettingsComposition[readOnlyIndex] = {
                        ...generalSettingsComposition[readOnlyIndex],
                        isDisabled: data && !data.visible && !data.mobileVisibility,
                    };
                    generalSettingsComposition[visibleInPhoneCallFormIndex] = {
                        ...generalSettingsComposition[visibleInPhoneCallFormIndex],
                        isDisabled: data && !data.visible && !data.mobileVisibility,
                    };
                    generalSettingsComposition[mandatoryRestApiIndex] = {
                        ...generalSettingsComposition[mandatoryRestApiIndex],
                        isHidden: !data?.restApi,
                    };

                    const composition = schema.current.layout.composition.map((item) => {
                        if (item.schemaId === 'generalSettings') {
                            return {
                                ...generalSettings,
                                composition: generalSettingsComposition,
                            };
                        }
                        return item;
                    });

                    const newSchema = {
                        ...schema.current,
                        layout: {
                            ...schema.current.layout,
                            composition,
                        },
                    };

                    schema.current = newSchema;

                    if (!data.visible && !data.mobileVisibility) {
                        data.mandatory = false;
                        data.readOnly = false;
                        data.visibleInPhoneCallForm = false;
                    }

                    dispatch({ type: 'changeData', data });
                    break;
                case 'relevantFields':
                    if (entityLogSubmodule === 'accountsTab')
                        logEvent({
                            event: entityLogName,
                            submodule: entityLogSubmodule,
                            functionality: 'featuredField',
                        });
                    dispatch({ type: 'changeData', data });
                    break;
                case 'customValidation':
                    dispatch({ type: 'changeData', data });
                    break;
                default:
                    dispatch({ type: 'changeData', data });
                    break;
            }
        },
        [
            fieldsToHide,
            state.crudType,
            state.data?.fieldName,
            state.data?.entity?.value,
            state.fields,
            language,
            t,
            fieldsData,
            handleToggleSmartFields,
            entityLogSubmodule,
            logEvent,
            entityLogName,
            customGeneralTab,
        ],
    );

    const onBlur = useCallback(
        (data, field) => {
            if (
                field === 'description' &&
                (state?.crudType === CRUD_TYPE_ADD || state?.crudType === CRUD_TYPE_DUPLICATE)
            ) {
                dispatch({
                    type: 'changeData',
                    data: {
                        fieldName: normalizeFieldName(data.description),
                    },
                });
            }
            if (field === 'fieldName') {
                dispatch({
                    type: 'changeData',
                    data: {
                        fieldName: normalizeFieldName(data.fieldName),
                    },
                });
            }
        },
        [state?.crudType],
    );

    const steps = [
        {
            selector: '.fs-form__section-checkDuplicates',
            title: t('cfm_label_promotion_duplicates'),
            description: t('cfm_label_promotion_duplicates_body'),
        },
    ];

    const onCloseTour = useCallback(() => {
        if (customOnCloseTour) {
            customOnCloseTour('duplicateFields');
        } else {
            const deviceCacheDuplicateFields = deviceCache?.duplicateFields || {};
            setDeviceCache({
                ...deviceCache,
                duplicateFields: { ...deviceCacheDuplicateFields, duplicateButton: true },
            });
        }
    }, [customOnCloseTour, deviceCache, setDeviceCache]);

    const shouldShowTour = useMemo(() => {
        if (avoidTours) return false;
        // Duplicates is enabled
        if (
            ENABLED_DUPLICATE_ENTITIES.includes(state?.data?.entity?.value) &&
            state?.data?.type?.value === 'text'
        ) {
            if (overrideTours && overrideTours.hasOwnProperty('duplicateFields'))
                return !overrideTours.duplicateFields;
            const deviceCacheDuplicateFields = deviceCache?.duplicateFields || {};
            if (deviceCacheDuplicateFields?.duplicateButton) return false;
            return true;
        }
        return false;
    }, [
        deviceCache?.duplicateFields,
        overrideTours,
        state?.data?.entity?.value,
        state?.data?.type?.value,
        avoidTours,
    ]);

    useLayoutEffect(() => {
        if (shouldShowTour) {
            setTimeout(() => {
                document
                    .querySelector(`.fs-form__section-checkDuplicates`)
                    ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }, 500);
        }
    }, [shouldShowTour]);

    const defaultValues = useMemo(() => {
        if (
            !state?.data ||
            !Object.keys(state?.data)?.length ||
            (!state?.data?.fieldName && !state?.data?.description)
        )
            return {};
        return {
            description: state.data.fieldName || state.data.description,
            descriptionBR: '',
            descriptionDE: '',
            descriptionDK: '',
            descriptionEN: '',
            descriptionFR: '',
            descriptionIT: '',
            descriptionPT: '',
            descriptionRU: '',
            descriptionUS: '',
            name: normalizeFieldName(state.data.fieldName || state.data.description),
            apiNameV4: null,
        };
    }, [state?.data]);

    const onAfterSaveTabId = useCallback(
        (value) => {
            onChange({ ...(state.data || {}), tabId: value }, 'tabId');
            dispatch({ type: 'changeTabs', tabs: [...(state?.valueLists?.tabs || {}), value] });
        },
        [state?.data, onChange, state?.valueLists],
    );

    const onClickAdvanced = useCallback(() => {
        if (onClickGoToAdmin) return onClickGoToAdmin();
        return onOpenUpgradeAdminInfo();
    }, [onClickGoToAdmin, onOpenUpgradeAdminInfo]);

    const finalOverrides = useMemo(
        () => ({
            ...(overrides || {}),
            fieldsToHide: overrides?.valueListFieldsToHide || [],
            columnsToHide: overrides?.valueListColumnsToHide || [],
            fieldsToAvoidExpand: overrides?.valueListFieldsToAvoidExpand || [],
        }),
        [overrides],
    );

    return (
        <Fragment>
            <HyperDrawer
                isOpen={state.isOpen}
                locales={locales}
                isLoading={state.isLoading}
                isSaving={state.isSaving}
                onCancel={onCancel}
                onSave={onSave}
                onSaveCreate={
                    !hideSaveAndCreateAnother && state.crudType === CRUD_TYPE_ADD && onSaveCreate
                }
                stackIndex={stackIndex}
                data={state.data}
                originalData={state.originalData}
                className="fields__extra-field-form"
            >
                {shouldShowTour && <Tour steps={steps} onClose={onCloseTour} delay={1000} />}
                <ForceForm
                    values={state.data}
                    errors={state.errors}
                    onChange={onChange}
                    onBlur={onBlur}
                    schema={schema.current}
                    mode="create"
                    className={classes.root}
                    modifyFieldProps={modifyFieldProps}
                    stackIndex={stackIndex}
                    loadedValues={state?.loadedValues}
                />
                {isWeb && (
                    <Button type="secondary" icon="settings" isFullWidth onClick={onClickAdvanced}>
                        {t('action_advanced_options')}
                    </Button>
                )}
            </HyperDrawer>
            <FormValueList
                onRef={(ref) => (formValueLists.current = ref)}
                onSave={onSaveValueListForm}
                onStack={setStackIndex}
                overrides={finalOverrides}
                initialData={defaultValues}
                isSage={isSage}
            />
            <FormFieldsValidations
                onRef={(ref) => (formValidations.current = ref)}
                onSave={onSaveFieldValidationForm}
                stackIndex={stackIndex}
                onStack={setStackIndex}
            />
            <FormTab
                onRef={(ref) => (formTab.current = ref)}
                refreshList={refreshList}
                fieldsData={state.fieldsData}
                getEntityData={getEntityData}
                idSetupRole={idSetupRole}
                setActiveRowId={setActiveRowId}
                overrides={{
                    ...overrides,
                    fieldsToHide:
                        overrides?.isWeb && overrides?.formTabFieldsToHide?.length
                            ? overrides.formTabFieldsToHide
                            : [],
                }}
                onStack={setStackIndex}
                afterOnSave={onAfterSaveTabId}
            />
            <FormSmartFieldConfig
                onRef={(ref) => (formSmartFieldConfig.current = ref)}
                onSave={handleSaveSmartFieldConfig}
                onStack={setStackIndex}
            />
            <SmartFieldsConfirmModal
                isOpen={showSmartFieldsConfirmModal}
                onConfirm={handleConfirmSmartFields}
                onCancel={handleCancelSmartFieldsConfirm}
            />
        </Fragment>
    );
};

FormExtraField.propTypes = {
    onRef: PropTypes.func.isRequired,
    refreshList: PropTypes.func.isRequired,
    allFields: PropTypes.object,
    getEntityData: PropTypes.func.isRequired,
};

export default memo(FormExtraField);
